import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { UserPicker } from '../common';

const ManageOrgAdminsModal = ({ organization, onClose, onUpdate }) => {
  const [orgUsers, setOrgUsers] = useState([]);
  const [selectedAdmins, setSelectedAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadOrgUsers();
  }, [organization.id]);

  const loadOrgUsers = async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/admin/users?organization_id=${organization.id}`, { headers });
      setOrgUsers(response.data);
      
      // Initialize selected admins with current admin users
      setSelectedAdmins(organization.admin_user_ids || []);
    } catch (error) {
      console.error('Failed to load organization users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminToggle = (userId) => {
    setSelectedAdmins(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/admin/organizations/${organization.id}`, {
        admin_user_ids: selectedAdmins
      }, { headers });
      
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Failed to update organization admins:', error);
      alert('Failed to update organization admins: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ui-ink bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-ui-surface">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-ui-ink">Manage Organization Admins</h3>
              <p className="text-sm text-ui-ink-3">{organization.name}</p>
            </div>
            <button
              onClick={onClose}
              className="text-ui-muted hover:text-ui-ink-3 text-xl"
            >
              ×
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ui-primary mx-auto"></div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-xs text-ui-ink-3 mb-3">
                  Only users who are members of "{organization.name}" can be designated as admins for this organization.
                </p>

                <UserPicker
                  users={orgUsers.map(u => ({
                    ...u,
                    name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email
                  }))}
                  selectedIds={selectedAdmins}
                  onChange={setSelectedAdmins}
                  label="Select Admin Users from Organization Members"
                  placeholder="Search users by name or email..."
                  showRole
                  excludeIds={orgUsers.filter(u => u.role === 'superadmin').map(u => u.id)}
                  maxHeight={240}
                  loading={loading}
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t border-ui-line">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-ui-ink-3 bg-ui-hover rounded-md hover:bg-ui-line"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-ui-primary hover:bg-ui-primary-hover text-ui-on-primary rounded-md disabled:opacity-50"
                >
                  {saving ? 'Updating...' : 'Update Admin Users'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageOrgAdminsModal;